import React, { memo, useCallback, useMemo } from 'react';
import SaveFilterSetLink from '../ControlTower/components/SaveFilterSetLink';
import { FilterArrayItem } from '../ControlTower/types';
import AlertFilterPills from './AlertFilterPills';
import CarouselSlider from './CarouselSlider';
import { sectionMap } from './CustomAlertsFilterData';
import Pill from './Pill';
import { buildDateLabel, toLabel } from './utils/labels';
import { normalizeFilterLogic, supportsFilterLogic } from './filterLogic';
import { DIRECT_SHIPMENT_FILTER_KEYS } from './utils/directFilters';
import { getUserDateFormat } from '@/common/utils/utils';

const aiAlertIds = ['processing', 'needs_attention', 'resolved_via_ai'];

interface AppliedFilter {
  id: string;
  name?: string;
  title?: string;
  courier_name?: string;
  label?: string;
  value?: string;
  filterBy?: string;
  dateFilter?: string;
  groupTitle?: string;
  start?: string;
  end?: string;
  [key: string]: unknown;
}

interface RenderPillItem {
  key: string;
  id?: string;
  label: string;
  filterBy: string;
  opensPanel: boolean;
  removeAll: boolean;
  onRemove: () => void;
}

interface PillListProps {
  appliedFilters?: AppliedFilter[];
  onResetAll?: () => void;
  // Dropdown-only filters (AI state, sentiment, Type) are active, so Reset all applies without pills.
  hasSelectionFilters?: boolean;
  removeAppliedFilter: (filter: AppliedFilter | AppliedFilter[]) => void;
  onPillClick?: (
    filterBy: string,
    open: boolean,
    target?: EventTarget,
    id?: string
  ) => void;
  filterArray: FilterArrayItem[];
  onOpen?: () => void;
  isSaved?: boolean;
  showSaveFilterSetLink?: boolean;
  /** When true, pills wrap into multiple rows instead of a horizontal scroller */
  blockLayout?: boolean;
  className?: string;
}

// Custom date-range pills start/end re-labeled here
// date_format preference even after it changes mid-session.
const getText = (o: AppliedFilter) => {
  const isCustomDateRange =
    !!o?.dateFilter &&
    (!!o?.start || !!o?.end) &&
    (!o?.period || o?.period === 'Custom');
  if (isCustomDateRange) {
    return buildDateLabel(
      o.dateFilter as string,
      (o.start as string) ?? null,
      (o.end as string) ?? null,
      getUserDateFormat()
    );
  }
  return o?.name || o?.title || o?.courier_name || o?.label || o?.value || '';
};

function PillList({
  appliedFilters = [],
  onResetAll,
  hasSelectionFilters = false,
  removeAppliedFilter,
  onPillClick,
  filterArray,
  onOpen,
  showSaveFilterSetLink = true,
  isSaved,
  blockLayout = false,
  className = '',
}: PillListProps) {
  const nonAlertPills = useMemo(
    () =>
      appliedFilters.filter(
        (f) =>
          !sectionMap.alert_state.includes(f.id) &&
          !sectionMap.critical_alerts.includes(f.id) &&
          !sectionMap.at_risk_alerts.includes(f.id) &&
          !aiAlertIds.includes(f.id)
      ),
    [appliedFilters]
  );

  const showResetAll = useMemo(() => {
    const keys = new Set(
      appliedFilters.map((f) => f.filterBy || f.dateFilter || 'unknown')
    );
    return hasSelectionFilters || keys.size > 1 || appliedFilters.length > 1;
  }, [appliedFilters, hasSelectionFilters]);

  const groupedByFilter = useMemo(() => {
    return nonAlertPills.reduce((acc: Record<string, AppliedFilter[]>, f) => {
      const key = f.filterBy || f.dateFilter || 'unknown';
      (acc[key] ||= []).push(f);
      return acc;
    }, {});
  }, [nonAlertPills]);

  const renderPills = useMemo(() => {
    const items: RenderPillItem[] = [];
    Object.entries(groupedByFilter).forEach(([fb, group]) => {
      const groupTitle =
        group.find((g) => g.groupTitle)?.groupTitle || toLabel(fb);
      const displayGroupTitle =
        supportsFilterLogic(fb) &&
        normalizeFilterLogic(group[0]?.logic) === 'AND'
          ? `${groupTitle} (And)`
          : groupTitle;
      const isDateKeyNoGroup =
        fb === 'scheduled_pickup_date' ||
        fb === 'scheduled_delivery_date' ||
        fb === 'customer_defined_ship_date' ||
        fb === 'created_at';

      if (group.length > 1 && fb !== 'unknown' && !isDateKeyNoGroup) {
        const firstText = getText(group[0]) || groupTitle;
        items.push({
          key: `group-${fb}`,
          label: `${displayGroupTitle}: ${firstText} + ${group.length - 1}`,
          filterBy: fb,
          opensPanel: true,
          removeAll: true,
          onRemove: () => removeAppliedFilter(group),
        });
      } else {
        group.forEach((it, idx) =>
          items.push({
            key: `${it.id}-${idx}`,
            id: it.id,
            label:
              supportsFilterLogic(fb) &&
              normalizeFilterLogic(it.logic) === 'AND'
                ? `${displayGroupTitle}: ${getText(it) || toLabel(it.filterBy)}`
                : getText(it) || toLabel(it.filterBy),
            filterBy: fb,
            opensPanel: !DIRECT_SHIPMENT_FILTER_KEYS.has(fb),
            removeAll: false,
            onRemove: () => removeAppliedFilter(it),
          })
        );
      }
    });
    return items;
  }, [groupedByFilter, removeAppliedFilter]);

  const handleClick = useCallback(
    (pill: RenderPillItem) => (e: React.MouseEvent) => {
      if (!pill.opensPanel) return;
      onPillClick?.(pill.filterBy, true, e.currentTarget, pill.id);
    },
    [onPillClick]
  );

  if (!appliedFilters.length && !hasSelectionFilters) return null;

  const pillNodes = (
    <>
      {renderPills?.map((pill) => (
        <div key={pill?.key} style={{ flex: '0 0 auto' }}>
          <Pill
            label={pill?.label}
            onClick={pill?.opensPanel ? handleClick(pill) : undefined}
            onRemove={pill?.onRemove}
          />
        </div>
      ))}
      <AlertFilterPills
        appliedFilters={appliedFilters}
        removeAppliedFilter={removeAppliedFilter}
        onOpenGroup={(event) =>
          onPillClick?.('alert_types', true, event.currentTarget)
        }
      />
      {showResetAll && !blockLayout && (
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            marginLeft: 8,
            marginTop: 7,
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            userSelect: 'none',
            flex: '0 0 auto',
          }}
          onClick={(e) => {
            e.stopPropagation?.();
            onResetAll?.();
          }}
        >
          Reset all
        </span>
      )}
      {showSaveFilterSetLink && (
        <SaveFilterSetLink
          appliedFilters={appliedFilters}
          filterArray={filterArray}
          onOpen={onOpen}
          isSaved={isSaved}
        />
      )}
    </>
  );

  if (blockLayout) {
    return (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          width: '100%',
          flexDirection: 'column',
        }}
        className='filter-responsive-gap'
      >
        {pillNodes}
      </div>
    );
  }

  return (
    <div
      style={{ width: '100%', flex: 1, minWidth: 0, overflowX: 'auto' }}
      className={`filter-responsive-gap ${className}`}
    >
      <CarouselSlider>{pillNodes}</CarouselSlider>
    </div>
  );
}

export default memo(PillList);
